/**
 * Der Umriss einer Person — die Punktliste, die aus der Wischgeste von Schritt 3
 * übrig bleibt (ARCHITEKTUR.md 6.2, 6.5).
 *
 * ⚠️ Diese Datei importiert bewusst NICHTS, aus demselben Grund wie `shared/keys.ts`:
 * sie wird vom Browser **und** vom Server gelesen.
 *
 * Hier steht nur, was **beide** Seiten wissen müssen — die Form und die Grenzen. Das
 * Rechnen (Ausdünnen, Vereinfachen, Hüllrechteck) steht im Browser, in
 * `src/lib/umriss.ts`: der Server bekommt das Ergebnis und prüft es, er erzeugt es nie.
 */

/** Ein Punkt in Prozent, `[x, y]` — nicht `{x, y}`: 60 Umrisse à 12 Punkte gehen als
 *  Paare durch die Leitung, nicht als 1.440 Feldnamen. */
export type Punkt = [number, number];

/**
 * Ein geschlossener Umriss. Der letzte Punkt wird **nicht** wiederholt — `clip-path:
 * polygon()` schließt von selbst, und ein wiederholter Punkt wäre eine zweite Wahrheit
 * über dieselbe Kante.
 *
 * ⚠️ **Prozent wovon?** Vom **aufrecht dekodierten** Bild, also von den Maßen, die als
 * `width`/`height` in `images` stehen. Sie stammen aus
 * `createImageBitmap(..., { imageOrientation: "from-image" })` (`src/lib/vorschau.ts`),
 * tragen die EXIF-Drehung also bereits eingerechnet.
 *
 * Das ist **nicht** dasselbe wie „Prozent der Originaldatei": die Datei im Speicher
 * trägt ihr Drehflag weiterhin, `src/lib/gps.ts` entfernt nur Ortsdaten. Wer den
 * Umriss später auf die rohe Datei legt, ohne die Drehung anzuwenden, schneidet bei
 * jedem hochkant aufgenommenen Handyfoto quer. Steht so in ARCHITEKTUR.md 6.5.
 */
export type Umriss = Punkt[];

/**
 * Weniger als drei Punkte umschließen keine Fläche. Das ist der eine Notausgang aus
 * ARCHITEKTUR.md 6.2; der zweite — „fast keine Fläche" — braucht die Bildmaße und
 * steht deshalb im Browser.
 */
export const MIN_UMRISS_PUNKTE = 3;

/**
 * Höchstens 40 Punkte (ARCHITEKTUR.md 6.2). Bei 3 % Toleranz kommen rund 12 heraus;
 * 40 ist die Reißleine für ein Bild, dessen Umriss sich nicht weiter vereinfachen
 * lässt, und zugleich die Obergrenze, gegen die der Server prüft.
 */
export const MAX_UMRISS_PUNKTE = 40;

/**
 * Nachkommastellen, auf die jeder gespeicherte Wert gerundet wird.
 *
 * Eine. Bei 3 % Toleranz ist alles darunter Rauschen — und das Abnahmekriterium von
 * Schritt 3 verlangt, dass der Umriss beim Wiederöffnen **unverändert** zurückkommt.
 * Gerundet wird auf **beiden** Seiten: der Browser rundet, bevor er zeichnet und
 * sendet, der Server rundet, bevor er speichert. Die Rundung ist idempotent, also
 * ändert der zweite Lauf nichts — aber sie macht die gespeicherte Form unabhängig
 * davon, was ein Client geschickt hat.
 */
export const UMRISS_STELLEN = 1;

const FAKTOR = 10 ** UMRISS_STELLEN;

/** Ein Wert auf `UMRISS_STELLEN` gerundet und in 0–100 eingesperrt. */
export function rundeWert(wert: number): number {
  const eingesperrt = Math.min(100, Math.max(0, wert));
  return Math.round(eingesperrt * FAKTOR) / FAKTOR;
}

/** Derselbe Umriss, gerundet. Idempotent — zweimal angewendet ändert nichts. */
export function rundeUmriss(umriss: Umriss): Umriss {
  return umriss.map(([x, y]) => [rundeWert(x), rundeWert(y)]);
}

function istPunkt(value: unknown): value is Punkt {
  if (!Array.isArray(value) || value.length !== 2) return false;
  return value.every(
    (zahl) => typeof zahl === "number" && Number.isFinite(zahl) && zahl >= 0 && zahl <= 100,
  );
}

/**
 * Prüft, was über die Leitung kam.
 *
 * Bewusst streng bei der **Form** und großzügig beim **Inhalt**: ob jemand eine
 * krakelige Linie oder ein sauberes Polygon gezogen hat, geht den Server nichts an —
 * „grob" ist die Zusage (ARCHITEKTUR.md 6.2), und eine Selbstüberschneidungsprüfung
 * würde Umrisse abweisen, die in `clip-path` völlig in Ordnung aussehen.
 *
 * Was er prüft, ist alles, was ihn später beißen kann: die Zahl der Punkte (sonst
 * wächst eine Zeile unbegrenzt), der Wertebereich (sonst steht ein `clip-path` mit
 * -1e9 % in der Seite) und der Typ (sonst landet `null` in einer Rechnung).
 */
export function istGueltigerUmriss(value: unknown): value is Umriss {
  if (!Array.isArray(value)) return false;
  if (value.length < MIN_UMRISS_PUNKTE || value.length > MAX_UMRISS_PUNKTE) return false;
  return value.every(istPunkt);
}
